import { useState, useEffect } from 'react';
import {
  Modal, Stack, TextInput, Select, Button, Group, Alert, Badge, Text, Loader,
} from '@mantine/core';
import { IconAlertCircle, IconSearch, IconCheck } from '@tabler/icons-react';
import { api } from '../../apiClient';

function EditCertificationModal({ opened, cert, onClose, onSuccess }) {
  const [name, setName]                    = useState('');
  const [formalName, setFormalName]        = useState('');
  const [code, setCode]                    = useState('');
  const [vendor, setVendor]                = useState('');
  const [examGuideUrl, setExamGuideUrl]    = useState('');
  const [urlKeyword, setUrlKeyword]        = useState('');
  const [suggestions, setSuggestions]     = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);

  useEffect(() => {
    if (cert) {
      setName(cert.name ?? '');
      setFormalName(cert.formal_name ?? '');
      setCode(cert.code ?? '');
      setVendor(cert.vendor ?? '');
      setExamGuideUrl(cert.exam_guide_url ?? '');
      setUrlKeyword(cert.url_keyword ?? '');
      setSuggestions([]);
      setError(null);
    }
  }, [cert]);

  const handleClose = () => {
    setSuggestions([]);
    setError(null);
    onClose();
  };

  const fetchSuggestions = async () => {
    setSuggestLoading(true);
    setSuggestions([]);
    try {
      const data = await api.get(`/certifications/${cert.id}/url-keyword-suggestions`);
      setSuggestions(data.suggestions ?? []);
      if (!data.suggestions?.length) setError('候補が見つかりませんでした。手動で入力してください。');
      else setError(null);
    } catch (e) {
      setError('候補の取得に失敗しました。');
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      await api.put(`/certifications/${cert.id}`, {
        name: name.trim() || null,
        formal_name: formalName.trim() || null,
        code: code.trim() || null,
        vendor: vendor || null,
        exam_guide_url: examGuideUrl.trim() || null,
        url_keyword: urlKeyword.trim() || null,
      });
      handleClose();
      onSuccess();
    } catch (e) {
      if (e?.status === 409) {
        setError('このコードは既に使用されています。');
      } else {
        setError('更新に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!cert) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`資格を編集 — ${cert.name}`}
      size="lg"
    >
      <Stack gap="sm">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        <TextInput
          label="名称"
          description="略称（例: SAA-C03）"
          placeholder="SAA-C03"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />

        <TextInput
          label="正式名称"
          description="フル名称（例: AWS Solutions Architect - Associate）"
          placeholder="AWS Solutions Architect - Associate"
          value={formalName}
          onChange={(e) => setFormalName(e.currentTarget.value)}
        />

        <TextInput
          label="コード"
          description="小文字の識別コード（例: saac03）。UNIQUE 制約あり"
          placeholder="saac03"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
        />

        <Select
          label="ベンダー"
          data={[
            { value: 'aws', label: 'AWS' },
            { value: 'microsoft', label: 'Microsoft' },
            { value: 'google', label: 'Google' },
          ]}
          value={vendor}
          onChange={setVendor}
          allowDeselect={false}
        />

        <TextInput
          label="試験ガイド URL"
          description="公式試験ガイドの URL"
          placeholder="https://aws.amazon.com/jp/certification/..."
          value={examGuideUrl}
          onChange={(e) => setExamGuideUrl(e.currentTarget.value)}
        />

        <Group gap="xs" align="flex-end">
          <TextInput
            style={{ flex: 1 }}
            label="ExamTopics URL キーワード"
            description="スクレイパーが URL から資格を判別するキーワード（例: az-900）"
            placeholder="az-900"
            value={urlKeyword}
            onChange={(e) => setUrlKeyword(e.currentTarget.value)}
          />
          <Button
            variant="default"
            leftSection={suggestLoading ? <Loader size={14} /> : <IconSearch size={14} />}
            onClick={fetchSuggestions}
            disabled={suggestLoading}
            style={{ marginBottom: 2 }}
          >
            候補を取得
          </Button>
        </Group>

        {suggestions.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">Discussions から検出されたキーワード（クリックで選択）：</Text>
            <Group gap="xs">
              {suggestions.map(({ keyword, count }) => (
                <Badge
                  key={keyword}
                  size="md"
                  variant={urlKeyword === keyword ? 'filled' : 'outline'}
                  color="blue"
                  style={{ cursor: 'pointer', textTransform: 'none' }}
                  leftSection={urlKeyword === keyword ? <IconCheck size={12} /> : null}
                  onClick={() => setUrlKeyword(keyword)}
                >
                  {keyword} ({count})
                </Badge>
              ))}
            </Group>
          </Stack>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleClose} disabled={loading}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            保存
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditCertificationModal;
